import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Box, Typography, IconButton, CircularProgress } from "@mui/material";
import { IconChevronLeft } from "@tabler/icons-react";
import { assembliesApi } from "../api";
import { barIconSx, cardSx, ds, dsText, shellSx, topBarSx } from "../theme/keeperTheme";


function formatDayLabel(isoDate) {
  if (!isoDate || typeof isoDate !== "string") return "—";
  const [y, m, d] = isoDate.split("-").map((x) => parseInt(x, 10));
  if (!y || !m || !d) return isoDate;
  try {
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString("ru-RU", {
      weekday: "short",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return isoDate;
  }
}

export default function ScanHistoryPage() {
  const { registerToolbarRefresh } = useOutletContext() || {};
  const navigate = useNavigate();
  const [days, setDays] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await assembliesApi.scanHistory({ days: 90 });
      setDays(Array.isArray(data.days) ? data.days : []);
      setTotal(typeof data.total === "number" ? data.total : 0);
    } catch (e) {
      setError(e?.message || "Ошибка загрузки");
      setDays([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!registerToolbarRefresh) return undefined;
    return registerToolbarRefresh(() => load());
  }, [registerToolbarRefresh, load]);

  return (
    <Box sx={shellSx}>
      <Box
        sx={{
          ...topBarSx,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 1.5,
          py: 1.25,
        }}
      >
        <IconButton
          onClick={() => navigate("/", { replace: false })}
          aria-label="Назад в меню"
          sx={barIconSx}
        >
          <IconChevronLeft size={26} stroke={1.5} />
        </IconButton>
        <Typography sx={{ color: ds.text, fontWeight: 700, flex: 1 }}>
          История
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          px: 2,
          py: 1.5,
        }}
      >
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={36} sx={{ color: ds.primary }} />
          </Box>
        )}
        {!loading && error && (
          <Typography sx={{ color: dsText.danger, textAlign: "center", py: 4 }}>{error}</Typography>
        )}
        {!loading && !error && days.length === 0 && (
          <Typography sx={{ color: ds.text2, textAlign: "center", py: 6 }}>
            Пока нет успешно отсканированных маркировок
          </Typography>
        )}
        {!loading && !error && days.length > 0 && (
          <>
            <Typography
              sx={{
                color: ds.text2,
                fontSize: "0.8rem",
                mb: 1.25,
                fontWeight: 500,
              }}
            >
              Всего успешных сканирований:{" "}
              <Box component="span" sx={{ color: ds.primaryHover, fontWeight: 700 }}>
                {total}
              </Box>
            </Typography>
            {days.map((row) => (
              <Box
                key={row.date}
                sx={{
                  ...cardSx,
                  mb: 1,
                  py: 1.2,
                  px: 1.4,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Typography
                  sx={{
                    color: ds.text,
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    lineHeight: 1.35,
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {formatDayLabel(row.date)}
                </Typography>
                <Typography
                  sx={{
                    color: ds.primaryHover,
                    fontSize: "0.9375rem",
                    fontWeight: 800,
                    fontFeatureSettings: '"tnum"',
                    flexShrink: 0,
                  }}
                >
                  {row.count} шт
                </Typography>
              </Box>
            ))}
          </>
        )}
      </Box>
    </Box>
  );
}
